import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import type { AdminBoundaryInfo } from '../../services/api';

export interface SearchableBoundaryInputProps {
  label: string;
  value: string;
  onChange: (name: string, meta?: { id?: string }) => void;
  /** Load options for the current query string (may be empty for "all" when minQueryLength is 0). */
  fetchOptions: (query: string) => Promise<AdminBoundaryInfo[]>;
  /** Minimum characters before calling fetch (0 = fetch on focus / empty query). */
  minQueryLength?: number;
  /** If true, value can only be set by picking from the dropdown — typing only filters. */
  strict?: boolean;
  placeholder?: string;
  disabled?: boolean;
  hint?: string;
  inputClass: string;
  labelClass: string;
}

/**
 * Search-as-you-type field backed by admin boundaries API.
 * In strict mode, value is set only from dropdown picks; typing is for filtering.
 * In free mode (default), typing directly sets the value.
 */
export function SearchableBoundaryInput({
  label,
  value,
  onChange,
  fetchOptions,
  minQueryLength = 0,
  strict = false,
  placeholder,
  disabled,
  hint,
  inputClass,
  labelClass,
}: SearchableBoundaryInputProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<AdminBoundaryInfo[]>([]);
  const [highlight, setHighlight] = useState(0);
  // In strict mode, `query` is the filter text typed by the user while `value` holds the confirmed pick.
  const [query, setQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep display/filter text in sync when value is set externally (edit pre-fill, auto-fill).
  useEffect(() => {
    if (!strict || !open) setQuery(value);
  }, [value, open, strict]);

  const runFetch = useCallback(
    async (q: string) => {
      if (minQueryLength > 0 && q.trim().length < minQueryLength) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const items = await fetchOptions(q.trim());
        setOptions(Array.isArray(items) ? items : []);
        setHighlight(0);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions, minQueryLength]
  );

  const searchTerm = strict ? query : value;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!open) return;
      runFetch(searchTerm);
    }, 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, open, runFetch]);

  const handleFocus = () => {
    if (blurRef.current) clearTimeout(blurRef.current);
    setOpen(true);
    // In strict mode, if already picked show all options; otherwise filter by current query
    runFetch(strict && value ? '' : searchTerm);
  };

  const handleBlur = () => {
    blurRef.current = setTimeout(() => {
      setOpen(false);
      // In strict mode, revert query to the confirmed value on blur
      if (strict) setQuery(value);
    }, 180);
  };

  const pick = (item: AdminBoundaryInfo) => {
    onChange(item.name, { id: item.id });
    setQuery(item.name);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (strict) {
      setQuery(v);
      // If the user clears the field, also clear the picked value
      if (!v.trim()) onChange('');
    } else {
      onChange(v);
    }
    if (!open) setOpen(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(options[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      if (strict) setQuery(value);
    }
  };

  const displayValue = strict ? (open ? query : value) : value;

  return (
    <div ref={containerRef} className="relative">
      <label className={labelClass}>{label}</label>
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          autoComplete="off"
          className={inputClass}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${label}-listbox`}
        />
        {strict && value && !open && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"
            title="Clear"
          >
            <X size={14} />
          </button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
          </div>
        )}
      </div>
      {hint && <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">{hint}</p>}
      {open && options.length > 0 && (
        <ul
          id={`${label}-listbox`}
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-lg py-1"
        >
          {options.map((opt, i) => (
            <li key={opt.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`w-full text-left px-3 py-2 text-sm ${i === highlight
                  ? 'bg-primary-50 dark:bg-primary-950/50 text-gray-900 dark:text-neutral-100'
                  : 'text-gray-800 dark:text-neutral-200 hover:bg-gray-50 dark:hover:bg-neutral-700/80'
                  }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt)}
              >
                <span className="font-medium">{opt.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
